import '../index.scss';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  message,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { requestWithAuth } from '../../services/api';
import CommonTable from '../../components/CommonTable';
import TopSection from '../../components/TopSection';

const opOptions = [
  { value: 'merge', label: 'merge' },
  { value: 'unmerge', label: 'unmerge' },
];

const CreateModal = ({ toggleShowModal, fetchPersonMergeData }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setIsLoading(true);
        setError(null);
        const body = {
          b2b_id: values.b2b_id,
          b2c_id: values.b2c_id,
          op: values.op,
        };
        requestWithAuth('vault-b2b-b2c-merge', 'POST', null, body)
          .then((response) => {
            setIsLoading(false);
            if (response && response.error) {
              setError(Object.values(response.error).join(', '));
              return;
            }
            if (response) {
              message.success('Person merge entry created');
              toggleShowModal(false);
              fetchPersonMergeData();
            }
          })
          .catch(() => {
            setIsLoading(false);
          });
      })
      .catch(() => {
        message.error('Please fill the mandatory fields');
      });
  };

  return (
    <Modal
      title="New Person Merge"
      open
      width={600}
      okText="Create"
      onOk={handleSubmit}
      confirmLoading={isLoading}
      onCancel={() => toggleShowModal(false)}
    >
      <Spin size="default" spinning={isLoading}>
        <Form
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          form={form}
          initialValues={{ op: 'merge' }}
        >
          <Form.Item
            name="b2b_id"
            label="b2b_id"
            rules={[{ required: true, message: 'Enter b2b_id' }]}
          >
            <Input placeholder="Enter b2b_id" autoComplete="_away" />
          </Form.Item>
          <Form.Item
            name="b2c_id"
            label="b2c_id"
            rules={[
              { required: true, message: 'Enter b2c_id' },
              {
                type: 'integer',
                message: 'b2c_id must be an integer',
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter b2c_id"
              precision={0}
              step={1}
              parser={(value) => (value ? Math.trunc(Number(value)) : value)}
            />
          </Form.Item>
          <Form.Item
            name="op"
            label="Operation"
            rules={[{ required: true, message: 'Select an operation' }]}
          >
            <Select options={opOptions} />
          </Form.Item>
          {error && <div className="error-text">{error}</div>}
        </Form>
      </Spin>
    </Modal>
  );
};

const PersonMerge = () => {
  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const { pageSize, current } = listPagination;
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [personMergeData, setPersonMergeData] = useState([]);
  const [personCount, setPersonCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortInfo, setSortInfo] = useState({ column: '', order: '' });
  const { column, order } = sortInfo;
  const sourceRef = useRef(null);

  const fetchPersonMergeData = useCallback(() => {
    setIsLoading(true);

    interface PersonMergeRequestParams {
      page_size?: number;
      page?: number;
      q?: string;
      ordering?: string;
    }

    const personMergeParams: PersonMergeRequestParams = {
      page_size: pageSize,
      page: current,
    };
    if (searchQuery) personMergeParams.q = searchQuery;
    if (column && order)
      personMergeParams.ordering = order === 'descend' ? '-' + column : column;

    if (sourceRef.current)
      sourceRef.current.cancel('Operation canceled by the user.');
    const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
    const source = CancelToken.source();
    sourceRef.current = source;

    requestWithAuth(
      'vault-b2b-b2c-merge',
      'GET',
      personMergeParams,
      null,
      source.token,
    ).then((response) => {
      // A newer fetch replaced this one; let it manage the state.
      if (sourceRef.current !== source) return;
      if (response && response.results) {
        setPersonMergeData(response.results);
        setPersonCount(response.count);
        setListPagination((prevState) => ({
          ...prevState,
          total: response.count,
        }));
      }
      setIsLoading(false);
    });
  }, [pageSize, current, searchQuery, column, order]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchPersonMergeData();
  }, [fetchPersonMergeData]);

  const handleTableChange = (pagination, filtersArg, sorter) => {
    setSelectedRowKeys([]);
    if (sorter && sorter.order && sorter.columnKey) {
      setSortInfo({
        column: sorter.columnKey,
        order: sorter.order,
      });
    } else {
      setSortInfo({
        column: '',
        order: '',
      });
    }
    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const toggleShowModal = (flag) => {
    setShowCreateModal(flag);
  };

  const handleSearchQuery = (searchValue) => {
    setSearchQuery(searchValue || '');
    setSelectedRowKeys([]);
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleFormReset = () => {
    setSearchQuery('');
    setSelectedRowKeys([]);
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleDelete = (ids) => {
    const body = {
      vault_b2b_b2c_merge_ids: ids,
    };
    setIsLoading(true);
    requestWithAuth(
      'vault-b2b-b2c-merge/delete-multiple',
      'POST',
      null,
      body,
    ).then((response) => {
      if (response && response.code === 200)
        message.success('Person merge data deleted');
      setSelectedIds([]);
      setSelectedRowKeys([]);
      setIsLoading(false);
      fetchPersonMergeData();
    });
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    const selectedIds = selectedRows.map((obj) => obj.id);
    setSelectedRowKeys(selectedRowKeys);
    setSelectedIds(selectedIds);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
    },
    {
      title: 'b2b_id',
      dataIndex: 'b2b_id',
      key: 'b2b_id',
      width: 250,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'b2b_id' && sortInfo.order,
      render: (val) => (
        <div
          style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
        >
          {val}
        </div>
      ),
    },
    {
      title: 'b2c_id',
      dataIndex: 'b2c_id',
      key: 'b2c_id',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'b2c_id' && sortInfo.order,
    },
    {
      title: 'Operation',
      dataIndex: 'op',
      key: 'op',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'op' && sortInfo.order,
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 170,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'created' && sortInfo.order,
      render: (val) => (val ? dayjs(val).format('MM/DD/YYYY HH:mm') : ''),
    },
    {
      title: 'Updated',
      dataIndex: 'updated',
      key: 'updated',
      width: 170,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'updated' && sortInfo.order,
      render: (val) => (val ? dayjs(val).format('MM/DD/YYYY HH:mm') : ''),
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
      render: (text, record) => (
        <Popconfirm title="Remove?" onConfirm={() => handleDelete([record.id])}>
          <span
            style={{ color: 'var(--color-primary)' }}
            onClick={(e) => e.preventDefault()}
          >
            Remove
          </span>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div className="people-editor">
        <TopSection
          count={personCount}
          showSearch
          placeholder="Search b2b_id, b2c_id, or op"
          handleSearchQuery={handleSearchQuery}
          handleFormReset={handleFormReset}
          searchValue={searchQuery}
          toggleCreateForm={toggleShowModal}
          deleteButtonText={'Remove'}
          selectedRowKeys={selectedRowKeys}
          handleDeleteMultiple={() => handleDelete(selectedIds)}
        />
        <Spin size="default" spinning={isLoading}>
          <CommonTable
            columns={columns}
            data={personMergeData}
            onTableChange={handleTableChange}
            pagination={{ ...listPagination }}
            pageSize={listPagination.pageSize}
            loading={isLoading}
            rowKey={(rec) => rec.id}
            rowSelection={rowSelection}
            selectedRowKeys={selectedRowKeys}
            scroll={{ x: 'max-content' }}
          />
        </Spin>
      </div>
      {showCreateModal && (
        <CreateModal
          toggleShowModal={toggleShowModal}
          fetchPersonMergeData={fetchPersonMergeData}
        />
      )}
    </>
  );
};

export default PersonMerge;
